import type {
  ScaleLinear,
  ScaleLogarithmic,
  ScaleSymLog,
  ScaleTime,
} from 'd3-scale';
import { inRange } from 'lodash-es';

import { GRAIL_MAX_DOMAIN, GRAIL_MIN_DOMAIN } from '../constants.js';
import { A11yToolbarTabIndex } from '../types/toolbar.js';
import { ChartZoomConfig } from '../types/zoom.js';

export type PanDirection = 'left' | 'right';
export type PanStepType = 'small' | 'large';

const PAN_LARGE_STEP = 0.02;
const PAN_SMALL_STEP = 0.01;

/**
 * Checks if new domain is the initial zoom state.
 * @param newDomain - The targeted new domain.
 * @param initialDomain - The initial domain.
 * @returns A boolean that compares domains to verify if the zoom-out action reaches the initial zoom state.
 */
export function isDomainMinZoom(
  newDomain: [number, number],
  infiniteZoom: boolean,
  initialDomain?: [number, number],
) {
  if (newDomain[0] <= +GRAIL_MIN_DOMAIN && newDomain[1] >= +GRAIL_MAX_DOMAIN) {
    return true;
  }

  if (initialDomain === undefined || infiniteZoom) {
    return false;
  }

  if (
    (newDomain[0] === initialDomain[0] && newDomain[1] !== initialDomain[1]) ||
    (newDomain[0] !== initialDomain[0] && newDomain[1] === initialDomain[1])
  ) {
    return false;
  }

  return !(
    inRange(newDomain[0], initialDomain[0], initialDomain[1]) &&
    inRange(newDomain[1], initialDomain[0], initialDomain[1])
  );
}

/**
 * Checks if new domain is the maximum possible.
 * @param newDomain - The targeted new domain.
 * @returns A boolean that compares domain min and max values to verify if the zoom-in action reaches the max zoom limit.
 */
export function isDomainMaxZoom(
  newDomain: [number, number],
  initialDomain: [number, number],
): boolean {
  const newMinValue = newDomain[0];
  const newMaxValue = newDomain[1];

  const isEqualDomain = newMinValue === newMaxValue;
  if (isEqualDomain) {
    return true;
  }

  return initialDomain[0] > initialDomain[1]
    ? newMaxValue > newMinValue
    : newMinValue > newMaxValue;
}

/**
 * Get if it is at min or max zoom.
 *
 * @param initialDomain - the initial domain as number (number or timestamp) without zoom
 * @param nextDomain - the domain as number (number or timestamp) to be zoomed in
 * @param infiniteZoom - Enables or disabled the infiniteZoom.
 * @returns - if it is at min or max zoom.
 */
export function isZoomBoundaries(
  initialDomain: [number, number],
  nextDomain: [number, number],
  infiniteZoom: boolean,
) {
  const isMinZoom = isDomainMinZoom(nextDomain, infiniteZoom, initialDomain);
  const isMaxZoom = isDomainMaxZoom(nextDomain, initialDomain);

  return { isMinZoom, isMaxZoom };
}

/**
 * Handles a new panning move.
 * @param xScale - The axis scale.
 * @param panDirection - The direction of the panning movement.
 * @param stepType - The scale of the panning.
 * @param initialXScaleDomain - The initial scale domain.
 * @returns The new domain.
 */
export const handlePanningMovement = (
  xScale:
    | ScaleLinear<number, number>
    | ScaleLogarithmic<number, number>
    | ScaleTime<number, number>
    | ScaleSymLog<number, number>,
  panDirection: PanDirection,
  stepType: PanStepType,
  initialXScaleDomain?: number[] | Date[],
) => {
  const [, width] = xScale.range();
  const directionFactor = panDirection === 'left' ? 1 : -1;
  const panStep =
    (stepType === 'small' ? PAN_SMALL_STEP : PAN_LARGE_STEP) * width;

  return panningMove(xScale, initialXScaleDomain, directionFactor * panStep);
};

/**
 * Calculates panning move given a domain and delta
 * @param xScale - Current scale of X-axis
 * @param initialXScaleDomain - Initial x-axis scale.
 * @param delta - Amount to pan: if positive sign, goes left; otherwise right
 * @returns void
 */
const panningMove = (
  xScale:
    | ScaleLinear<number, number>
    | ScaleLogarithmic<number, number>
    | ScaleTime<number, number>
    | ScaleSymLog<number, number>,
  initialXScaleDomain: number[] | Date[] | undefined,
  delta: number,
) => {
  const [rangeMin, rangeMax] = xScale.range();
  const [initialMin, initialMax] = initialXScaleDomain ?? [];
  const newMin = xScale.invert(rangeMin - delta);
  const newMax = xScale.invert(rangeMax - delta);

  if (
    (+newMin > +initialMin && +newMax < +initialMax) ||
    !initialXScaleDomain
  ) {
    return [newMin, newMax];
  }

  return [xScale.invert(rangeMin), xScale.invert(rangeMax)];
};

interface ConfigProps {
  zoomX?: ChartZoomConfig;
  pan?: ChartZoomConfig;
  zoom?: ChartZoomConfig;
  download?: { enabled: boolean };
  intent?: boolean;
}

export function getA11yToolbarConfig(config: ConfigProps) {
  const { zoom, zoomX, download, intent } = config;

  const a11YToolbar: A11yToolbarTabIndex[] = [
    ...[{ explore: 0 }],
    ...(zoom?.enabled ? [{ 'zoom-in': -1 }] : []),
    ...(zoom?.enabled ? [{ 'zoom-out': -1 }] : []),
    ...(zoom?.enabled || zoomX?.enabled ? [{ reset: -1 }] : []),
    ...(download?.enabled ? [{ download: -1 }] : []),
    ...(intent ? [{ intent: -1 }] : []),
    ...[{ collapse: -1 }],
  ];

  return a11YToolbar;
}

/**
 * Checks if currentDomain and initialDomain are equal
 * @param currentDomain - The minimum and maximum date of the current domain.
 * @param initialDomain - The initial domain.
 * @returns boolean value
 */

export function isSameDomain(
  currentDomain: (number | Date)[] | undefined,
  initialDomain: (number | Date)[],
) {
  if (currentDomain === undefined) {
    return true;
  }
  return (
    +currentDomain[0] === +initialDomain[0] &&
    +currentDomain[1] === +initialDomain[1]
  );
}
